import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { controlTask, type AgentTask } from './service';
import { button, primary, errorText, useCan, Notice } from './ui';
import { useToastStore } from '../../store/toast.store';

type Action = 'paused' | 'active' | 'offline';
const actions: { status: Action; label: string; description: string }[] = [
  { status: 'paused', label: '暂停新领取', description: '暂停后会员不能新领取此任务；已领取记录仍按原有流程提交、审核和发放奖励。' },
  { status: 'active', label: '恢复已批准版本', description: '恢复前会校验商户权限与当前版本的审核结果；已结束或名额已满的任务仍受原规则限制。' },
  { status: 'offline', label: '下架任务', description: '下架后停止新领取；已领取记录仍按原有流程处理。' },
];

export function AgentTaskControls({ task, onChanged }: { task: AgentTask; onChanged: () => void }) {
  const canControl = useCan('agent_tasks.control');
  const addToast = useToastStore(s => s.addToast);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const pending = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const current = actions.find(item => item.status === action);
  useEffect(() => {
    if (action && !dialog.current?.open) dialog.current?.showModal();
  }, [action]);
  if (!canControl || !task.formal_task_id || task.status === 'pending_review') return null;
  const available = actions.filter(item => item.status === 'active'
    ? task.status === 'paused' || task.status === 'offline'
    : item.status === 'paused'
      ? task.status === 'approved' || task.status === 'active'
      : task.status !== 'offline');
  const submit = async () => {
    if (!action || !reason.trim() || pending.current || conflict) return;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      await controlTask(task, action, reason.trim());
      addToast('success', action === 'paused' ? '任务已暂停新领取' : action === 'active' ? '已恢复批准版本' : '任务已下架');
      setAction(null);
      onChanged();
    } catch (e) {
      setError(errorText(e));
      setConflict(Boolean(e && typeof e === 'object' && 'message' in e && e.message === 'version_conflict'));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return <>
    {available.map(item => <button key={item.status} type="button" className={button} onClick={() => {
      setReason(''); setError(''); setConflict(false); setAction(item.status);
    }}>{item.label}</button>)}
    {current && createPortal(<dialog
      ref={dialog}
      aria-label={current.label}
      onCancel={event => { event.preventDefault(); if (!pending.current) setAction(null); }}
      className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto rounded-lg border-0 p-6 shadow-lg backdrop:bg-black/30"
    >
      <form className="space-y-4" onSubmit={event => { event.preventDefault(); void submit(); }}>
        <h3 className="text-base font-medium">{current.label}</h3>
        <p className="break-words text-sm text-gray-700">{task.payload.title}</p>
        <p className="text-sm text-gray-500">{current.description}</p>
        <label className="block space-y-2 text-sm">操作原因（必填）
          <textarea autoFocus required maxLength={2000} rows={3} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} className="block w-full rounded-md border border-gray-300 p-3" />
        </label>
        <Notice error={error} />
        <div className="flex flex-wrap justify-end gap-3">
          <button type="button" className={button} disabled={busy} onClick={() => setAction(null)}>取消</button>
          {conflict ? <button type="button" className={primary} onClick={() => { setAction(null); onChanged(); }}>刷新任务状态</button>
            : <button type="submit" className={primary} disabled={busy || !reason.trim()}>{busy ? '处理中…' : '确认操作'}</button>}
        </div>
      </form>
    </dialog>, document.body)}
  </>;
}
